import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Card, MultiSelect, tokens } from '@hotjar/hotsauce';
import type { MultiSelectProps } from '@hotjar/hotsauce';
import {
  DEFAULT_LABEL_ICONS,
  LabelsEditor,
  useAvailableLabels,
  useCreateLabel,
} from '@hotjar/labels';
import type { LabelModel } from '@hotjar/labels';

import { LabelsContext } from '@hotjar/webapp/contexts/labels';
import type { Organization } from '@hotjar/organization';
import type { IndexedMentionItem } from '@hotjar/comments/utils/content-utils';
import { parse, resolveMentionDetails } from '@hotjar/comments/utils/content-utils';
import { Textarea } from '@hotjar/comments/components/Textarea';
import type { TimelineActiveItem } from '@hotjar/webapp/features/Playbacks/types';
import { ItemType } from '@hotjar/webapp/features/Playbacks/types';
import type { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';

import type { HighlightCreatorProps } from '../HighlightCreator';
import { HighlightCreator } from '../HighlightCreator';
import type { HighlightPopoverContainer } from '../HighlightPopoverContainer';
import { PopoverHeaderText } from '../PopoverHeaderText';
import { useRecordingHighlightCreate } from './use-recording-highlight-create';

type MultiSelectOptions = MultiSelectProps['selectedOptions'];

const StyledCard = styled(Card)`
  text-align: left;
`;

const LabelsEditorWithMargin = styled(LabelsEditor)`
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x2};
`;

const LabelsMultiSelect = styled(MultiSelect)`
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x2};

  z-index: 2;
  div[data-tippy-root] {
    position: relative;
    z-index: 2;
    :hover {
      position: relative;
      z-index: 2;
    }
  }
`;

const mapLabelsToMultiSelectOptions = (labels?: Array<LabelModel>): MultiSelectOptions => {
  return (
    labels?.map((label) => ({
      label: label.name,
      value: label.id,
      icon:
        label.iconName !== 'unknown'
          ? DEFAULT_LABEL_ICONS[label.iconName] ?? label.iconName
          : undefined,
    })) ?? []
  );
};

interface NewHighlightPopoverProps {
  currentOrganization: Organization | undefined;
  deactivateCurrentItem: React.ComponentProps<typeof HighlightPopoverContainer>['onClose'];
  newHighlightTime: React.ComponentProps<typeof HighlightCreator>['newHighlightTime'];
  newHighlightTimeError: React.ComponentProps<typeof HighlightCreator>['newHighlightTimeError'];
  onNewHighlightTime: React.ComponentProps<typeof HighlightCreator>['onNewHighlightTime'];
  recordingId: number; // FIXME: id should always be a string
  setActiveItem: (item: TimelineActiveItem) => void;
  showSuccessMessage: ReturnType<typeof useNotify>['success'];
  siteId: string | null;
  jumpTo: HighlightCreatorProps['jumpTo'];
  onPlay: HighlightCreatorProps['onPlay'];
  pause: HighlightCreatorProps['pause'];
  loaded: HighlightCreatorProps['loaded'];
  isPlaying: HighlightCreatorProps['isPlaying'];
}

export function NewHighlightPopover({
  currentOrganization,
  deactivateCurrentItem,
  newHighlightTime,
  newHighlightTimeError,
  recordingId,
  setActiveItem,
  onNewHighlightTime,
  showSuccessMessage,
  siteId,
  jumpTo,
  onPlay,
  pause,
  loaded,
  isPlaying,
}: NewHighlightPopoverProps): JSX.Element | null {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [comment, setComment] = useState<[string, Array<IndexedMentionItem>]>(['', []]);
  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(new Set());
  const { isLoading, mutateAsync: createRecordingHighlight } = useRecordingHighlightCreate(siteId);
  const { t } = useTranslation();
  const users = currentOrganization?.users ?? [];
  const { click: trackClick } = useTrack();

  const { hasPermission } = useAuthorization();
  const hasOrgInvitePermission = hasPermission(
    'organization',
    `${currentOrganization?.id}`,
    'org-invite',
  );

  // THESE ARE FOR NEW CUSTOM LABELS
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');
  const { createLabel } = useCreateLabel(Number(siteId));
  const { data: labels } = useAvailableLabels(siteId, 'all');
  const [availableLabels, setAvailableLabels] = useState<MultiSelectOptions>(
    mapLabelsToMultiSelectOptions(labels),
  );

  useEffect(() => {
    setAvailableLabels(mapLabelsToMultiSelectOptions(labels));
  }, [labels, setAvailableLabels]);

  const [selectedLabels, setSelectedLabels] = useState<MultiSelectOptions>([]);

  const onLabelsChange = (selectedOptions: MultiSelectOptions) => {
    if (selectedOptions !== undefined) {
      setSelectedLabels(selectedOptions);
      setSelectedLabelIds(new Set(selectedOptions.map((label) => label.value)));
    }
  };

  const onAddCustomLabel = async (name: string) => {
    const newLabel = await createLabel(name.charAt(0).toUpperCase() + name.slice(1));

    if (newLabel && selectedLabels !== undefined) {
      setSelectedLabels([
        ...(selectedLabels ?? []),
        { value: String(newLabel.id), label: newLabel.name },
      ]);
      setSelectedLabelIds(new Set(selectedLabels.map((label) => label.value)));

      onLabelsChange([
        ...(selectedLabels ?? []),
        { value: String(newLabel.id), label: newLabel.name },
      ]);
    }
  };
  // END OF CUSTOM LABELS

  if (!newHighlightTime || !currentOrganization) {
    return null;
  }

  const handleSave: React.ComponentProps<typeof HighlightCreator>['onSave'] = async () => {
    const newComment = parse(comment[0], resolveMentionDetails(comment[1], users));
    const { id } = await createRecordingHighlight({
      highlightTime: newHighlightTime.marker,
      labels: Array.from(selectedLabelIds),
      recordingId,
      startTime: newHighlightTime.start,
      endTime: newHighlightTime.end,
      comment: newComment.length > 0 ? newComment : undefined,
      uiPosition: 'highlight bulb',
    });

    deactivateCurrentItem();
    // FIXME: id should always be a string
    setActiveItem({ type: ItemType.HIGHLIGHT, id: Number(id) });
    showSuccessMessage(t('highlights:save.highlight-created'));
  };

  return (
    <StyledCard onDismiss={deactivateCurrentItem} className="highlight-popover">
      <Card.Body className="card-body">
        <PopoverHeaderText as="p" textType="subtitle2">
          {t('highlights:recordings.create')}
        </PopoverHeaderText>
        {!isCustomLabelsAllowed ? (
          <LabelsContext>
            <LabelsEditorWithMargin
              siteId={siteId}
              selectedLabelIds={selectedLabelIds}
              onSelectedIdsChange={setSelectedLabelIds}
              onAdd={() =>
                trackClick('Create highlight - label click', 'clicked_on_popup_label', {})
              }
            />
          </LabelsContext>
        ) : (
          <LabelsMultiSelect
            size="s"
            availableOptions={availableLabels}
            selectedOptions={selectedLabels}
            placeholder={t('highlights:popover.add-label')}
            onChange={onLabelsChange}
            hasAddOption
            onAddOption={onAddCustomLabel}
          />
        )}
        <Textarea
          hasOrgInvitePermission={hasOrgInvitePermission}
          data-testid="comment"
          inputPlaceholder={t('comment:create.placeholder')}
          onChange={(text, mentions) => {
            setComment([text, mentions]);
          }}
          organizationName={currentOrganization.name}
          ref={textAreaRef}
          users={users}
          value={comment[0]}
        />
        <HighlightCreator
          highlightType="recording"
          isSubmitting={isLoading}
          newHighlightTimeError={newHighlightTimeError}
          newHighlightTime={newHighlightTime}
          onNewHighlightTime={onNewHighlightTime}
          onSave={handleSave}
          onClose={deactivateCurrentItem}
          jumpTo={jumpTo}
          onPlay={onPlay}
          pause={pause}
          loaded={loaded}
          isPlaying={isPlaying}
        />
      </Card.Body>
    </StyledCard>
  );
}
